import { Alert as AntAlert } from "antd";
import { useAppDispatch } from "core/utils";
import i18n from "i18next";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import styled, { css } from "styled-components";

import { hideNotification } from "../../core/setup/redux/state/notifications/notificationsState";
import type { AlertProps } from "./types";

export const NotificationAlert: FC<AlertProps> = ({ description, titleKey, type, isCloseable = true }) => {
    const dispatch = useAppDispatch();

    // Scroll to the top of the page when the notification is displayed
    useEffect(
        () =>
            document.getElementById("pageContent")?.scrollTo({
                top: 0,
                behavior: "smooth",
            }),
        [],
    );

    // Parse the description to check if it contains multiple errors
    const formattedDescription = useMemo(() => {
        if (!description) return description;

        // Split by newlines to check for multiple errors
        const errors = description.split("\n").filter((error) => error.trim());

        // If there are multiple errors, render them as a list
        if (errors.length > 1) {
            return (
                <ErrorList>
                    {errors.map((error) => (
                        <ErrorListItem key={error}>{error}</ErrorListItem>
                    ))}
                </ErrorList>
            );
        }

        // Single error - return as is
        return description;
    }, [description]);

    return (
        <StyledAlert
            type={type}
            message={titleKey ? i18n.t(titleKey) : null}
            // Description is already pre-formatted with all translations and arguments replaced
            description={formattedDescription}
            showIcon
            closable={isCloseable}
            onClose={() => dispatch(hideNotification())}
        />
    );
};

const AlertStyles = css`
    margin-bottom: 20px;
`;

const StyledAlert = styled(AntAlert)`
    ${AlertStyles}
`;

const ErrorList = styled.ul`
    margin: 0;
    padding-left: 20px;
`;

const ErrorListItem = styled.li`
    margin-bottom: 5px;
`;
